// =============================================================================
// EquipmentScreen — /equipment
// =============================================================================
// 22 Oct 2026 (mig 444) — rebuilt. James: "visually I want it re-done for the
// field guys on phone to see a really nice way of seeing equipment registers
// and not just boring tables but make it easy to use and visually appealing";
// "their gear first then the register"; "auditors … want exports … a way to
// filter easily and export for field and managers".
//
// Three tabs, phone-first, one register (v_equipment_register):
//   · Mine — the gear this person holds, as tag-coloured cards grouped by the
//     kit it lives in (kit → vehicle, or depot), plus the ladders kept in
//     their vehicle; one tap Inspect. Counts across the top.
//   · Register — everything (James, 12 Oct: everyone sees the full register),
//     search + chips (kind / standing / kit / vehicle / holder), Request on an
//     item someone else holds, Export (Excel / PDF, the road-week export).
//   · Needs a look — the person's own overdue / due / out-of-service gear and
//     their open requests; for an allocator (manager, director, supervisor)
//     also pending approvals, open requests with one-tap Allocate / Decline,
//     and open defects.
// Vehicles and radios/phones/IT still show on Mine in a small strip — they
// belong to HR → Assets, but the person holding them wants to see them here.
//
// Replaces the 8 Oct /equipment (own gear only, 60-day banding, ladders tab).
// Routes unchanged. The phone bottom bar now points here (Licences → Equipment).
// =============================================================================

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Search, Download, Plus, ChevronRight, Truck, Radio, HardHat, Wrench, AlignJustify,
  CheckCircle2, Hand, X,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Spinner, EmptyState, Button } from "@/components/ui";
import {
  type RegisterRow, type Kind, type Urgency, type EquipmentRequest,
  KIND_LABEL, KIND_PLURAL, URGENCY_LABEL, URGENCY_TONE, URGENCY_TEXT, URGENCY_CARD, URGENCY_ORDER,
  TagChip, standingLine, homeLine, itemHref, inspectHref, daysUntil, fmtDay,
  useEquipmentRegister, useEquipmentKits, useOpenRequests, useEquipmentActions,
  ExportDialog, AllocateDialog, ReasonSheet,
} from "./equipmentShared";

type Tab = "mine" | "register" | "attention";

const KIND_ICON: Record<Kind, typeof HardHat> = { rigging: HardHat, power_tool: Wrench, ladder: AlignJustify };

export default function EquipmentScreen() {
  const user = useRequireAuth();
  const me = user?.employee_id ?? null;
  const canAllocate = hasRole(user, "supervisor");   // director / manager / supervisor — app.can_allocate_equipment()
  const [tab, setTab] = useState<Tab>("mine");

  const { data: rows = [], isLoading } = useEquipmentRegister(!!user);
  const { data: kits = [] } = useEquipmentKits(!!user);
  const { data: requests = [] } = useOpenRequests(!!user);

  // the vehicles and radios / phones / IT this person holds — a small strip on Mine
  const { data: myVehicles = [] } = useQuery({
    queryKey: ["my_vehicles", me],
    enabled: !!me,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicles")
        .select("vehicle_id, vehicle_code, rego, make, model, rego_expiry, service_due_date, is_active")
        .eq("assigned_to_employee_id", me!)
        .eq("is_active", true)
        .order("vehicle_code");
      if (error) throw error;
      return (data ?? []) as unknown as { vehicle_id: string; vehicle_code: string; rego: string; make: string | null; model: string | null; rego_expiry: string | null; service_due_date: string | null }[];
    },
  });
  const { data: myOther = [] } = useQuery({
    queryKey: ["my_other_equipment", me],
    enabled: !!me,
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("v_my_assigned_equipment")
        .select("asset_id, asset_code, category, description, make_model, calibration_due")
        .neq("category", "ladder").order("asset_code");
      if (error) return [];
      return (data ?? []) as { asset_id: string; asset_code: string | null; category: string; description: string; make_model: string | null; calibration_due: string | null }[];
    },
  });
  // employees — only an allocator needs the list (for the Allocate dialog)
  const { data: employees = [] } = useQuery({
    queryKey: ["equipment_employees"],
    enabled: canAllocate,
    queryFn: async () => {
      const { data, error } = await supabase.from("employees").select("employee_id, first_name, last_name").eq("is_active", true).order("first_name");
      if (error) throw error;
      return (data ?? []) as { employee_id: string; first_name: string | null; last_name: string | null }[];
    },
  });

  const myVehicleCodes = useMemo(() => new Set(myVehicles.map(v => v.vehicle_code)), [myVehicles]);
  const mine = useMemo(() => rows.filter(r =>
    (me && r.holder_employee_id === me) || (r.kind === "ladder" && r.kept_in_vehicle_code && myVehicleCodes.has(r.kept_in_vehicle_code))
  ), [rows, me, myVehicleCodes]);

  if (!user) return null;
  if (!me) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card><div className="p-6 text-sm text-text-muted">Your login isn't linked to an employee record yet. Ask a director to complete setup.</div></Card>
      </div>
    );
  }

  const attentionCount = mine.filter(r => r.urgency !== "current").length
    + (canAllocate ? rows.filter(r => r.registration_status === "pending").length + requests.length : requests.filter(q => q.requested_by_employee_id === me).length);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto pb-28">
      <div className="mb-3">
        <h1 className="text-2xl font-semibold tracking-tight">Equipment</h1>
        <p className="text-[12px] text-text-muted mt-0.5">
          {tab === "mine" ? "Your gear first. Tap a card to inspect it." : tab === "register" ? "The whole register. Find a block or sling that isn't yours." : "What needs doing."}
        </p>
      </div>

      {/* tabs */}
      <div className="flex gap-1.5 mb-4">
        {([["mine", "Mine"], ["register", "Register"], ["attention", "Needs a look"]] as [Tab, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`flex-1 py-2 rounded-lg border text-[12px] font-medium transition-colors relative ${tab === k ? "bg-accent/15 border-accent text-accent" : "bg-ink-raised border-line text-text-muted hover:text-text"}`}>
            {label}
            {k === "attention" && attentionCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center">{attentionCount}</span>
            )}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-12 flex justify-center"><Spinner size={20}/></div>
      ) : tab === "mine" ? (
        <MineTab rows={mine} myVehicles={myVehicles} myOther={myOther} />
      ) : tab === "register" ? (
        <RegisterTab rows={rows} me={me} canAllocate={canAllocate} kits={kits} employees={employees} requests={requests}
          preparedByName={[user.first_name, user.last_name].filter(Boolean).join(" ") || user.login_email} />
      ) : (
        <AttentionTab rows={rows} mine={mine} me={me} canAllocate={canAllocate} requests={requests} kits={kits} employees={employees} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Mine
// -----------------------------------------------------------------------------

function MineTab({ rows, myVehicles, myOther }: {
  rows: RegisterRow[];
  myVehicles: { vehicle_id: string; vehicle_code: string; rego: string; make: string | null; model: string | null; rego_expiry: string | null; service_due_date: string | null }[];
  myOther: { asset_id: string; asset_code: string | null; category: string; description: string; make_model: string | null; calibration_due: string | null }[];
}) {
  const counts = countBy(rows);
  // group by home: kit (→ vehicle) · ladders kept in a vehicle · holding (no kit)
  const groups = useMemo(() => {
    const m = new Map<string, RegisterRow[]>();
    for (const r of rows) {
      const key = r.kind === "ladder" ? (r.kept_in_vehicle_code ? `Ladders · kept in ${r.kept_in_vehicle_code}` : "Ladders · depot")
                : r.kit_name ? `${r.kit_name}${r.kit_vehicle_code ? ` · in ${r.kit_vehicle_code}` : " · depot"}`
                : "Holding (not in a kit)";
      (m.get(key) ?? m.set(key, []).get(key)!).push(r);
    }
    for (const list of m.values()) list.sort(byUrgency);
    return Array.from(m.entries());
  }, [rows]);

  return (
    <>
      <div className="grid grid-cols-4 gap-1.5 mb-4">
        <Count n={counts.current} label="current" cls="text-ok" />
        <Count n={counts.due_soon} label="due soon" cls="text-warn" />
        <Count n={counts.overdue} label="overdue" cls="text-danger" />
        <Count n={counts.out_of_service} label="out of service" cls="text-danger" />
      </div>

      {rows.length === 0 ? (
        <Card><div className="p-5"><EmptyState title="Nothing allocated to you yet" description="Gear you hold shows here once a manager allocates it. Everything else is on the Register tab — Request what you need." icon={HardHat}/></div></Card>
      ) : groups.map(([home, list]) => (
        <div key={home} className="mb-4">
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1.5 px-0.5">{home}</div>
          <div className="space-y-2">
            {list.map(r => <ItemCard key={`${r.kind}-${r.item_id}`} r={r} mine />)}
          </div>
        </div>
      ))}

      <Link to="/inspections/add/rigging"
        className="block w-full rounded-xl border border-dashed border-line text-center text-[12px] text-text-muted py-3 hover:text-accent hover:border-accent/50 transition-colors">
        <Plus size={12} className="inline mr-1 -mt-0.5"/> Add gear I've got that isn't on the list
      </Link>

      {(myVehicles.length > 0 || myOther.length > 0) && (
        <div className="mt-6">
          <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1.5 px-0.5">Also yours</div>
          <div className="space-y-2">
            {myVehicles.map(v => {
              const d = Math.min(daysUntil(v.rego_expiry) ?? 9999, daysUntil(v.service_due_date) ?? 9999);
              const cls = d < 0 ? "text-danger" : d <= 30 ? "text-warn" : "text-text-muted";
              return (
                <Card key={v.vehicle_id}><div className="p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-ink-raised flex items-center justify-center text-text-muted shrink-0"><Truck size={16}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{[v.make, v.model].filter(Boolean).join(" ") || "Vehicle"} · {v.vehicle_code}</div>
                    <div className={`text-[11px] ${cls}`}>{v.rego}{v.rego_expiry ? ` · rego ${fmtDay(v.rego_expiry)}` : ""}{v.service_due_date ? ` · service ${fmtDay(v.service_due_date)}` : ""}</div>
                  </div>
                </div></Card>
              );
            })}
            {myOther.map(o => (
              <Card key={o.asset_id}><div className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-ink-raised flex items-center justify-center text-text-muted shrink-0"><Radio size={16}/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{o.description}{o.asset_code ? ` · ${o.asset_code}` : ""}</div>
                  <div className="text-[11px] text-text-muted">{o.make_model ?? o.category}{o.calibration_due ? ` · calibration ${fmtDay(o.calibration_due)}` : ""}</div>
                </div>
              </div></Card>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Register
// -----------------------------------------------------------------------------

function RegisterTab({ rows, me, canAllocate, kits, employees, requests, preparedByName }: {
  rows: RegisterRow[]; me: string; canAllocate: boolean;
  kits: { kit_id: string; kit_name: string; vehicle?: { vehicle_code: string } | null }[];
  employees: { employee_id: string; first_name: string | null; last_name: string | null }[];
  requests: EquipmentRequest[];
  preparedByName: string;
}) {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState<Kind | "all">("all");
  const [urg, setUrg] = useState<Urgency | "all">("all");
  const [kitId, setKitId] = useState<string | "all" | "pool">("all");
  const [holder, setHolder] = useState<string | "all" | "mine" | "none">("all");
  const [exportOpen, setExportOpen] = useState(false);
  const [allocating, setAllocating] = useState<RegisterRow | null>(null);
  const [requesting, setRequesting] = useState<RegisterRow | null>(null);
  const { request } = useEquipmentActions();
  const myOpen = useMemo(() => new Set(requests.filter(r => r.requested_by_employee_id === me).map(r => `${r.kind}-${r.item_id}`)), [requests, me]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r =>
      (kind === "all" || r.kind === kind)
      && (urg === "all" || r.urgency === urg)
      && (kitId === "all" || (kitId === "pool" ? (!r.kit_id && r.kind !== "ladder") : r.kit_id === kitId))
      && (holder === "all" || (holder === "mine" ? r.holder_employee_id === me : holder === "none" ? !r.holder_employee_id : r.holder_employee_id === holder))
      && (!needle || [r.code, r.asset_tag, r.display_name, r.serial_number, r.make, r.model, r.kit_name, r.holder_name, r.tag_number].some(v => (v ?? "").toLowerCase().includes(needle)))
    ).sort((a, b) => byUrgency(a, b) || a.code.localeCompare(b.code));
  }, [rows, q, kind, urg, kitId, holder, me]);

  const holders = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach(r => { if (r.holder_employee_id && r.holder_name) m.set(r.holder_employee_id, r.holder_name); });
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const scopeNote = [
    kind !== "all" ? KIND_PLURAL[kind] : null,
    urg !== "all" ? URGENCY_LABEL[urg] : null,
    kitId === "pool" ? "Pool" : kitId !== "all" ? `Kit: ${kits.find(k => k.kit_id === kitId)?.kit_name ?? ""}` : null,
    holder === "mine" ? "Mine" : holder === "none" ? "Unallocated" : holder !== "all" ? `Holder: ${holders.find(h => h[0] === holder)?.[1] ?? ""}` : null,
    q.trim() ? `Search: ${q.trim()}` : null,
  ].filter(Boolean).join(" · ");

  return (
    <>
      <div className="flex items-center gap-2 bg-ink-card border border-line rounded-xl px-3 py-2.5 mb-2">
        <Search size={14} className="text-text-muted shrink-0"/>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name, code, serial, tag no…" className="bg-transparent outline-none text-text text-[14px] flex-1 min-w-0"/>
        {q && <button type="button" onClick={() => setQ("")} className="text-text-muted"><X size={14}/></button>}
      </div>

      {/* chips — kind, standing */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-1 -mx-1 px-1">
        <Chip on={kind === "all"} onClick={() => setKind("all")}>All</Chip>
        {(["rigging", "power_tool", "ladder"] as Kind[]).map(k => <Chip key={k} on={kind === k} onClick={() => setKind(k)}>{KIND_PLURAL[k]}</Chip>)}
        <span className="w-px bg-line mx-0.5 shrink-0"/>
        {URGENCY_ORDER.map(u => <Chip key={u} on={urg === u} onClick={() => setUrg(urg === u ? "all" : u)} cls={URGENCY_TEXT[u]}>{URGENCY_LABEL[u]}</Chip>)}
      </div>
      {/* chips — kit, holder */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 mb-3 -mx-1 px-1">
        <Chip on={holder === "mine"} onClick={() => setHolder(holder === "mine" ? "all" : "mine")}>Mine</Chip>
        <Chip on={holder === "none"} onClick={() => setHolder(holder === "none" ? "all" : "none")}>Unallocated</Chip>
        <Chip on={kitId === "pool"} onClick={() => setKitId(kitId === "pool" ? "all" : "pool")}>Pool</Chip>
        {kits.map(k => <Chip key={k.kit_id} on={kitId === k.kit_id} onClick={() => setKitId(kitId === k.kit_id ? "all" : k.kit_id)}>{k.kit_name}{k.vehicle ? ` · ${k.vehicle.vehicle_code}` : ""}</Chip>)}
        {holders.length > 0 && (
          <select value={holder === "mine" || holder === "none" ? "all" : holder} onChange={e => setHolder(e.target.value as any)}
            className="shrink-0 bg-ink-raised border border-line rounded-full px-3 py-1 text-[11px] text-text-muted">
            <option value="all">Holder…</option>
            {holders.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card><div className="p-5"><EmptyState title="Nothing matches" description="Loosen the chips or the search." icon={Search}/></div></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <ItemCard key={`${r.kind}-${r.item_id}`} r={r} mine={r.holder_employee_id === me}
              requested={myOpen.has(`${r.kind}-${r.item_id}`)}
              onRequest={r.holder_employee_id !== me && !myOpen.has(`${r.kind}-${r.item_id}`) ? () => setRequesting(r) : undefined}
              onAllocate={canAllocate ? () => setAllocating(r) : undefined} />
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-2 text-[12px] text-text-muted">
        <span>{rows.length} items{filtered.length !== rows.length ? ` · filtered ${filtered.length}` : ""}</span>
        <Button size="sm" variant="secondary" icon={Download} onClick={() => setExportOpen(true)} disabled={filtered.length === 0}>Export Excel / PDF</Button>
      </div>

      {exportOpen && (
        <ExportDialog rows={filtered} domainLabel={kind === "all" ? "Equipment register" : KIND_PLURAL[kind]} defaultSite="" scopeNote={scopeNote} preparedByName={preparedByName} onClose={() => setExportOpen(false)} />
      )}
      {allocating && (
        <AllocateDialog row={allocating} kits={kits as any} employees={employees} onClose={() => setAllocating(null)} onDone={() => setAllocating(null)} />
      )}
      {requesting && (
        <ReasonSheet title={`Ask for ${requesting.code}`} blurb={`${requesting.display_name ?? ""} — ${requesting.holder_name ? `held by ${requesting.holder_name}` : homeLine(requesting)}. Every allocator gets a notification; one tap on their side allocates it to you.`}
          placeholder="What's it for? (optional) — e.g. Walcha job, Monday" cta="Send request" busy={request.isPending}
          onClose={() => setRequesting(null)}
          onSubmit={(text) => request.mutate({ kind: requesting.kind, item_id: requesting.item_id, note: text || undefined }, {
            onSuccess: () => setRequesting(null), onError: (e: any) => window.alert(e?.message ?? "Could not send the request") })} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// Needs a look
// -----------------------------------------------------------------------------

function AttentionTab({ rows, mine, me, canAllocate, requests, kits, employees }: {
  rows: RegisterRow[]; mine: RegisterRow[]; me: string; canAllocate: boolean; requests: EquipmentRequest[];
  kits: { kit_id: string; kit_name: string; vehicle?: { vehicle_code: string } | null }[];
  employees: { employee_id: string; first_name: string | null; last_name: string | null }[];
}) {
  const { approve, allocate, decline } = useEquipmentActions();
  const [allocating, setAllocating] = useState<RegisterRow | null>(null);
  const [declining, setDeclining] = useState<EquipmentRequest | null>(null);
  const byKey = useMemo(() => new Map(rows.map(r => [`${r.kind}-${r.item_id}`, r])), [rows]);

  const mineNeeding = mine.filter(r => r.urgency !== "current").sort(byUrgency);
  const myRequests = requests.filter(r => r.requested_by_employee_id === me);
  const pending = canAllocate ? rows.filter(r => r.registration_status === "pending") : [];
  const openReq = canAllocate ? requests : [];
  const openDefects = canAllocate ? rows.filter(r => r.open_defects > 0) : [];
  const teamNeeding = canAllocate ? rows.filter(r => r.urgency === "overdue" || r.urgency === "out_of_service").filter(r => !mine.includes(r)).sort(byUrgency) : [];

  const nothing = mineNeeding.length + myRequests.length + pending.length + openReq.length + openDefects.length + teamNeeding.length === 0;

  return (
    <>
      {nothing && <Card><div className="p-5"><EmptyState title="Nothing needs a look" description="Everything you hold is current and there's nothing waiting on you." icon={CheckCircle2}/></div></Card>}

      {mineNeeding.length > 0 && (
        <Section title="Your gear" count={mineNeeding.length}>
          {mineNeeding.map(r => <ItemCard key={`${r.kind}-${r.item_id}`} r={r} mine />)}
        </Section>
      )}

      {myRequests.length > 0 && (
        <Section title="Your open requests" count={myRequests.length}>
          {myRequests.map(q => {
            const r = byKey.get(`${q.kind}-${q.item_id}`);
            return (
              <Card key={q.request_id}><div className="p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-ink-raised flex items-center justify-center text-accent shrink-0"><Hand size={15}/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium truncate">{r ? `${r.display_name ?? r.code} · ${r.code}` : "Item"}</div>
                  <div className="text-[11px] text-text-muted">Asked {fmtDay(q.created_at, true)}{q.note ? ` · ${q.note}` : ""} · waiting on a manager</div>
                </div>
              </div></Card>
            );
          })}
        </Section>
      )}

      {openReq.length > 0 && (
        <Section title="Requests waiting on you" count={openReq.length}>
          {openReq.map(q => {
            const r = byKey.get(`${q.kind}-${q.item_id}`);
            const who = [q.requested_by?.first_name, q.requested_by?.last_name].filter(Boolean).join(" ") || "Someone";
            const forId = q.requested_for_employee_id ?? q.requested_by_employee_id;
            return (
              <Card key={q.request_id}><div className="p-3">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-accent/12 flex items-center justify-center text-accent shrink-0"><Hand size={15}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{who} wants {r ? `${r.display_name ?? r.code} · ${r.code}` : "an item"}</div>
                    <div className="text-[11px] text-text-muted">{fmtDay(q.created_at, true)}{q.note ? ` · ${q.note}` : ""}{r ? ` · now ${r.holder_name ? `with ${r.holder_name}` : homeLine(r).toLowerCase()}` : ""}</div>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  {r && (
                    <Button size="sm" variant="primary" disabled={allocate.isPending} className="flex-1 justify-center"
                      onClick={() => allocate.mutate({ kind: r.kind, item_id: r.item_id, holder_employee_id: forId, kit_id: r.kit_id, reason: q.note ?? undefined },
                        { onError: (e: any) => window.alert(e?.message ?? "Could not allocate") })}>
                      Allocate to {who.split(" ")[0]}
                    </Button>
                  )}
                  {r && <Button size="sm" variant="secondary" onClick={() => setAllocating(r)}>Other…</Button>}
                  <Button size="sm" variant="ghost" onClick={() => setDeclining(q)}>Decline</Button>
                </div>
              </div></Card>
            );
          })}
        </Section>
      )}

      {pending.length > 0 && (
        <Section title="Added by the crew — approve onto the register" count={pending.length}>
          {pending.map(r => (
            <div key={`${r.kind}-${r.item_id}`}>
              <ItemCard r={r} />
              <div className="flex gap-2 -mt-1 mb-2 px-1">
                <Button size="sm" variant="primary" disabled={approve.isPending} onClick={() => approve.mutate({ kind: r.kind, item_id: r.item_id }, { onError: (e: any) => window.alert(e?.message ?? "Could not approve") })}>
                  Approve{r.submitted_by_name ? ` (added by ${r.submitted_by_name.split(" ")[0]})` : ""}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => setAllocating(r)}>Approve + allocate</Button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {openDefects.length > 0 && (
        <Section title="Open defects" count={openDefects.length}>
          {openDefects.map(r => <ItemCard key={`${r.kind}-${r.item_id}`} r={r} />)}
        </Section>
      )}

      {teamNeeding.length > 0 && (
        <Section title="Overdue or out of service — everyone's gear" count={teamNeeding.length}>
          {teamNeeding.map(r => <ItemCard key={`${r.kind}-${r.item_id}`} r={r} onAllocate={() => setAllocating(r)} />)}
        </Section>
      )}

      {allocating && (
        <AllocateDialog row={allocating} kits={kits as any} employees={employees} onClose={() => setAllocating(null)}
          onDone={() => {
            // a pending item being allocated is approved on the way
            if (allocating.registration_status === "pending") approve.mutate({ kind: allocating.kind, item_id: allocating.item_id });
            setAllocating(null);
          }} />
      )}
      {declining && (
        <ReasonSheet title="Decline this request" placeholder="Why? (goes to the person)" cta="Decline" tone="danger" busy={decline.isPending}
          onClose={() => setDeclining(null)}
          onSubmit={(text) => decline.mutate({ request_id: declining.request_id, reason: text || undefined }, { onSuccess: () => setDeclining(null), onError: (e: any) => window.alert(e?.message ?? "Could not decline") })} />
      )}
    </>
  );
}

// -----------------------------------------------------------------------------
// bits
// -----------------------------------------------------------------------------

/** The card. Tag chip · name · where it lives · standing · one action. */
function ItemCard({ r, mine, requested, onRequest, onAllocate }: {
  r: RegisterRow; mine?: boolean; requested?: boolean; onRequest?: () => void; onAllocate?: () => void;
}) {
  const Icon = KIND_ICON[r.kind];
  const needsInspect = r.urgency === "overdue" || r.urgency === "due_soon" || r.urgency === "never_inspected";
  const out = r.urgency === "out_of_service";
  return (
    <div className={`rounded-xl border bg-ink-card p-3 flex items-center gap-3 ${URGENCY_CARD[r.urgency] || "border-line"}`}>
      <Link to={itemHref(r)} className="shrink-0" aria-label="Open item">
        {r.kind === "ladder" ? (
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${out ? "text-danger bg-danger/10 border border-dashed border-danger/50" : "bg-ink-raised text-text-muted"}`}><Icon size={18}/></div>
        ) : (
          <TagChip colour={r.tag_colour} number={r.tag_number} out={out}/>
        )}
      </Link>
      <Link to={itemHref(r)} className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold leading-tight truncate">
          {r.display_name ?? r.code} <span className="text-text-muted font-normal">· {r.code}</span>
          {r.registration_status === "pending" && <Pill label="pending" tone="notice" small/>}
          {r.open_defects > 0 && <span className="ml-1"><Pill label="defect open" tone="critical" small/></span>}
        </div>
        <div className="text-[11px] text-text-muted mt-0.5 truncate">
          {[KIND_LABEL[r.kind] === "Rigging" ? (r.item_class ?? "Rigging") : KIND_LABEL[r.kind], r.serial_number ? `S/N ${r.serial_number}` : null, r.swl_kg ? `SWL ${r.swl_kg} kg` : null].filter(Boolean).join(" · ")}
          {!mine && <> · {r.holder_name ? `with ${r.holder_name}` : homeLine(r)}</>}
          {mine && r.kind !== "ladder" && !r.kit_name && <> · pool</>}
        </div>
        <div className={`text-[11px] font-semibold mt-1 ${URGENCY_TEXT[r.urgency]}`}>{standingLine(r)}</div>
      </Link>
      <div className="shrink-0 flex flex-col gap-1 items-stretch">
        {onAllocate ? (
          <button type="button" onClick={onAllocate} className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-ink-raised border border-line text-text hover:border-accent">Allocate</button>
        ) : null}
        {mine ? (
          <Link to={inspectHref(r)} className={`px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-center ${needsInspect ? "bg-accent text-black" : "bg-ink-raised border border-line text-text"}`}>Inspect</Link>
        ) : onRequest ? (
          <button type="button" onClick={onRequest} className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-ink-raised border border-line text-text hover:border-accent">Request</button>
        ) : requested ? (
          <span className="px-2.5 py-1.5 rounded-lg text-[11px] text-accent border border-accent/40">Requested</span>
        ) : !onAllocate ? (
          <Link to={itemHref(r)} className="text-text-dim p-1"><ChevronRight size={16}/></Link>
        ) : null}
      </div>
    </div>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <div className="mb-5">
      <div className="text-[10px] font-mono uppercase tracking-wider2 text-text-muted mb-1.5 px-0.5">{title} <span className="text-text-dim">({count})</span></div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Count({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <div className="bg-ink-card border border-line rounded-lg py-2 text-center">
      <div className={`text-[18px] font-mono font-semibold leading-none ${n > 0 ? cls : "text-text-dim"}`}>{n}</div>
      <div className="text-[9px] uppercase tracking-wider2 text-text-muted mt-1">{label}</div>
    </div>
  );
}

function Chip({ on, onClick, children, cls }: { on: boolean; onClick: () => void; children: ReactNode; cls?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`shrink-0 px-3 py-1 rounded-full border text-[11px] whitespace-nowrap transition-colors ${on ? "bg-accent/12 border-accent text-accent" : `bg-ink-raised border-line ${cls && !on ? "" : ""} text-text-muted hover:text-text`}`}>
      {children}
    </button>
  );
}

function countBy(rows: RegisterRow[]) {
  const c: Record<Urgency, number> = { out_of_service: 0, overdue: 0, due_soon: 0, never_inspected: 0, current: 0 };
  rows.forEach(r => { c[r.urgency] = (c[r.urgency] ?? 0) + 1; });
  return c;
}

function byUrgency(a: RegisterRow, b: RegisterRow): number {
  const d = URGENCY_ORDER.indexOf(a.urgency) - URGENCY_ORDER.indexOf(b.urgency);
  if (d !== 0) return d;
  return (a.next_due ?? "9999").localeCompare(b.next_due ?? "9999");
}

